"use client";

import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Icons } from "@/components/icons";
import { statusLabel } from "@/lib/time";

const BTN_VARIANTS: Record<string, string> = {
  primary:
    "bg-indigo-600 hover:bg-indigo-700 text-white border-transparent",
  secondary:
    "bg-slate-100 hover:bg-slate-200 text-slate-800 border-transparent dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-100",
  outline:
    "bg-transparent hover:bg-slate-50 text-slate-700 border-slate-300 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-800",
  ghost:
    "bg-transparent hover:bg-slate-100 text-slate-700 border-transparent dark:text-slate-200 dark:hover:bg-slate-800",
  destructive:
    "bg-red-600 hover:bg-red-700 text-white border-transparent",
  "outline-destructive":
    "bg-transparent hover:bg-red-50 text-red-600 border-red-300 dark:hover:bg-red-950",
};

const BTN_SIZES: Record<string, string> = {
  xs: "h-6 px-2 text-xs gap-1",
  sm: "h-7 px-2.5 text-xs gap-1.5",
  md: "h-8 px-3 text-sm gap-2",
  lg: "h-9 px-4 text-sm gap-2",
};

export type BtnProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> & {
  variant?: keyof typeof BTN_VARIANTS;
  size?: keyof typeof BTN_SIZES;
  icon?: ReactNode;
  children?: ReactNode;
};

/**
 * Button look for elements that must not be a `<button>` — e.g. a real `<a>`
 * for an external link, where nesting a button inside the anchor would be
 * invalid HTML and leave `target="_blank"` up to the browser.
 */
export function btnClass({
  variant = "primary",
  size = "md",
  className = "",
}: {
  variant?: keyof typeof BTN_VARIANTS;
  size?: keyof typeof BTN_SIZES;
  className?: string;
} = {}): string {
  return `inline-flex items-center justify-center font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:opacity-50 disabled:pointer-events-none ${BTN_VARIANTS[variant ?? "primary"] ?? BTN_VARIANTS.primary} ${BTN_SIZES[size ?? "md"] ?? BTN_SIZES.md} ${className}`;
}

export function Btn({
  variant = "primary",
  size = "md",
  icon,
  children,
  className = "",
  type = "button",
  ...rest
}: BtnProps) {
  return (
    <button
      type={type}
      {...rest}
      className={btnClass({ variant, size, className })}
    >
      {icon && <span className="shrink-0 w-3.5 h-3.5">{icon}</span>}
      {children}
    </button>
  );
}

export function Card({
  children,
  className = "",
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg ${onClick ? "cursor-pointer hover:border-slate-300 dark:hover:border-slate-700 transition-colors" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 ${className}`}
    >
      {children}
    </div>
  );
}

export function CardTitle({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={`text-sm font-semibold text-slate-800 dark:text-slate-100 ${className}`}
    >
      {children}
    </h3>
  );
}

export function CardContent({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`p-4 ${className}`}>{children}</div>;
}

export function CardFooter({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`px-4 py-3 border-t border-slate-100 dark:border-slate-800 ${className}`}
    >
      {children}
    </div>
  );
}

export function Accordion({
  title,
  children,
  defaultOpen = false,
}: {
  /** Plain text, or a node when the header needs to carry a badge next to the label. */
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details
      open={defaultOpen}
      className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden group bg-white dark:bg-slate-900"
    >
      <summary className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/80 list-none flex justify-between items-center gap-2 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">{title}</span>
        <span className="text-slate-400 shrink-0 transition-transform group-open:rotate-90">
          {Icons.ChevronRight(18)}
        </span>
      </summary>
      <div className="border-t border-slate-200 dark:border-slate-800">
        {children}
      </div>
    </details>
  );
}

const BADGE_VARIANTS: Record<string, string> = {
  default: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  primary: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300",
  success: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  destructive: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  gray: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  blue: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  purple: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
  green:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
};

export function Badge({
  variant = "default",
  children,
  className = "",
}: {
  variant?: keyof typeof BADGE_VARIANTS;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${BADGE_VARIANTS[variant] ?? BADGE_VARIANTS.default} ${className}`}
    >
      {children}
    </span>
  );
}

const AVATAR_COLORS = [
  "bg-indigo-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-teal-500",
  "bg-cyan-500",
  "bg-sky-500",
  "bg-green-500",
];

function avatarColor(str: string) {
  return AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}

export function Avatar({
  initials = "?",
  size = "md",
  className = "",
}: {
  initials?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const sizes: Record<string, string> = {
    xs: "w-6 h-6 text-xs",
    sm: "w-7 h-7 text-xs",
    md: "w-8 h-8 text-sm",
    lg: "w-10 h-10 text-base",
    xl: "w-14 h-14 text-xl",
  };
  return (
    <div
      className={`${sizes[size] ?? sizes.md} rounded-full flex items-center justify-center font-semibold text-white shrink-0 ${avatarColor(initials)} ${className}`}
    >
      {initials.slice(0, 2).toUpperCase()}
    </div>
  );
}

const inputCls =
  "w-full h-8 px-3 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors";

export function Input({
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${inputCls} ${className}`} {...props} />;
}

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className = "", ...props }, ref) {
  // Vertical resize on purpose: several call sites hold long text the operator
  // has to read or write in full (Conocimiento at rows=12, políticas at rows=6,
  // the LLM prompts), and `rows` is only a starting height. Horizontal resize
  // stays off so a textarea can never break its form column. A call site that
  // must stay fixed — the inbox composer lives in a bounded bar — passes
  // ``resize-none`` itself.
  return (
    <textarea
      ref={ref}
      className={`w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors resize-y ${className}`}
      {...props}
    />
  );
});

export const SelectInput = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(function SelectInput({ className = "", children, ...props }, ref) {
  return (
    <select ref={ref} className={`${inputCls} ${className}`} {...props}>
      {children}
    </select>
  );
});

const filterSelectCls =
  "appearance-none shrink-0 min-w-[7rem] w-auto max-w-[11rem] h-8 pl-3 pr-7 text-xs font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer";

/** Compact toolbar filter — does not stretch (`w-full`); matches list filter chips. */
export function FilterSelect({
  className = "",
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative inline-flex shrink-0">
      <select className={`${filterSelectCls} ${className}`} {...props}>
        {children}
      </select>
      <span
        aria-hidden
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  size = "md",
  disabled,
  title,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  size?: "sm" | "md";
  disabled?: boolean;
  title?: string;
}) {
  const sizes = { sm: "w-7 h-4", md: "w-9 h-5" };
  const thumbSizes = { sm: "w-3 h-3", md: "w-4 h-4" };
  const translate = {
    sm: checked ? "translate-x-3" : "translate-x-0.5",
    md: checked ? "translate-x-4" : "translate-x-0.5",
  };
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onChange?.(!checked);
      }}
      className={`${sizes[size] ?? sizes.md} rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
        checked ? "bg-indigo-600" : "bg-slate-200 dark:bg-slate-700"
      } disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed`}
    >
      <span
        className={`block ${thumbSizes[size] ?? thumbSizes.md} bg-white rounded-full shadow transition-transform ${translate[size] ?? translate.md}`}
      />
    </button>
  );
}

export function Tabs({
  tabs,
  active,
  onChange,
  className = "",
  /** Distribute tabs evenly across the full tablist width. */
  fullWidth = false,
}: {
  tabs: { id: string; label: string; icon?: ReactNode }[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
  fullWidth?: boolean;
}) {
  return (
    <div
      className={`flex gap-1 min-w-0 border-b border-slate-200 dark:border-slate-800 ${
        fullWidth ? "w-full" : ""
      } ${className}`}
      role="tablist"
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          onClick={() => onChange(t.id)}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
            fullWidth
              ? "flex-1 min-w-0 justify-center"
              : "shrink-0"
          } ${
            active === t.id
              ? "border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          {t.icon ? <span className="shrink-0 opacity-80">{t.icon}</span> : null}
          <span className={fullWidth ? "truncate" : undefined}>{t.label}</span>
        </button>
      ))}
    </div>
  );
}

export function InfoBanner({
  variant = "blue",
  children,
  className = "",
}: {
  variant?: "blue" | "amber" | "red" | "success";
  children: ReactNode;
  className?: string;
}) {
  const vs = {
    blue: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-900 dark:text-blue-200",
    amber:
      "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950 dark:border-amber-900 dark:text-amber-200",
    red: "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-900 dark:text-red-200",
    success:
      "bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-900 dark:text-green-200",
  };
  return (
    <div
      className={`px-4 py-3 rounded-lg border text-sm ${vs[variant]} ${className}`}
    >
      {children}
    </div>
  );
}

export function Table({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  className = "",
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 ${className}`}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className = "",
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <td
      className={`px-3 py-2.5 text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800/70 ${className}`}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  className = "",
  onClick,
  hoverRow = false,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  /** CSS hover highlight on cells; independent of {@link onClick}. */
  hoverRow?: boolean;
}) {
  return (
    <tr
      onClick={onClick}
      className={`${
        hoverRow
          ? "[&:hover>td]:transition-colors [&:hover>td]:duration-150 [&:hover>td]:bg-slate-100 dark:[&:hover>td]:bg-slate-800 [&:hover>td]:border-b-slate-200 dark:[&:hover>td]:border-b-slate-600"
          : ""
      } ${onClick ? "cursor-pointer" : ""} ${className}`}
    >
      {children}
    </tr>
  );
}

export type DropdownItem =
  | "---"
  | {
      label: string;
      icon?: ReactNode;
      onClick?: () => void;
      destructive?: boolean;
    };

export function Dropdown({
  align = "right",
  trigger,
  items,
}: {
  align?: "left" | "right";
  trigger: ReactNode;
  items: DropdownItem[];
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className={`relative inline-flex ${open ? "z-40" : ""}`} ref={rootRef}>
      <span
        role="presentation"
        className="inline-flex"
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </span>
      {open ? (
        <div
          className={`absolute top-full mt-1 min-w-[10rem] py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {items.map((it, i) =>
            it === "---" ? (
              <div
                key={`d-${i}`}
                className="my-1 h-px bg-slate-100 dark:bg-slate-800"
              />
            ) : (
              <button
                key={`${it.label}-${i}`}
                type="button"
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800 ${
                  it.destructive
                    ? "text-red-600 dark:text-red-400"
                    : "text-slate-700 dark:text-slate-200"
                }`}
                onClick={() => {
                  it.onClick?.();
                  setOpen(false);
                }}
              >
                {it.icon ? (
                  <span className="shrink-0 w-3.5 h-3.5">{it.icon}</span>
                ) : null}
                {it.label}
              </button>
            )
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Panel width in px — mirrors `w-56` below; used to clamp against the viewport. */
const HINT_WIDTH = 224;

/**
 * Small "?" next to a label that explains how a number is calculated.
 *
 * Opens on hover **and on keyboard focus** (a hover-only hint is unreachable
 * with a keyboard and invisible on touch) and toggles on click, for touch.
 * `aria-describedby` is wired only while open, so a screen reader announces the
 * explanation on focus and stays quiet otherwise.
 *
 * **Rendered in a portal, `position: fixed`, clamped to the viewport.** An
 * absolutely-positioned panel next to a trigger near the right edge grows the
 * page's scroll width — and it does so even while hidden, because
 * `visibility: hidden` still occupies layout. That is a horizontal scrollbar on
 * any ancestor that scrolls (`app-shell`'s `<main>` has `overflow-y-auto`, which
 * forces the x axis to `auto` too). A fixed element positioned inside the
 * viewport contributes no scrollable overflow at all, and as a bonus it cannot
 * be clipped by a scroll container.
 *
 * ``z-[110]`` sits above ``Modal`` (``z-[100]``) so hints inside dialogs stay
 * visible; without that the portal opens behind the overlay and looks broken.
 */
export function InfoHint({
  children,
  label = "Cómo se calcula",
  className = "",
}: {
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null,
  );

  const open = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const box = trigger.getBoundingClientRect();
    const margin = 8;
    setPosition({
      top: box.bottom + 6,
      left: Math.min(
        Math.max(margin, box.left),
        window.innerWidth - HINT_WIDTH - margin,
      ),
    });
  }, []);
  const close = useCallback(() => setPosition(null), []);

  // The panel is anchored to viewport coordinates, so it would drift away from
  // its trigger on scroll or resize. Closing is simpler than re-measuring and
  // matches what every other hover affordance does.
  useEffect(() => {
    if (!position) return;
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [position, close]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={position ? id : undefined}
        aria-expanded={position !== null}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        onClick={() => (position ? close() : open())}
        className={`inline-flex align-middle rounded-full text-slate-300 hover:text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 dark:text-slate-600 dark:hover:text-slate-400 ${className}`}
      >
        {Icons.HelpCircle(14)}
      </button>
      {position !== null && typeof document !== "undefined"
        ? createPortal(
            <span
              id={id}
              role="tooltip"
              style={{ top: position.top, left: position.left, width: HINT_WIDTH }}
              className="pointer-events-none fixed z-[110] rounded-lg border border-slate-200 bg-white p-2.5 text-xs font-normal normal-case leading-relaxed tracking-normal text-slate-600 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
            >
              {children}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-slate-200 dark:bg-slate-800 rounded ${className}`}
    />
  );
}

/** Right-side drawer (inbox detail on small viewports). */
export function Sheet({
  open,
  onClose,
  title,
  children,
  side = "right",
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  side?: "right" | "left";
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !mounted) return null;

  const edge = side === "right" ? "right-0" : "left-0";

  return createPortal(
    <div
      className="fixed inset-0 z-[90]"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? "Panel"}
    >
      <div
        className="fixed inset-0 min-h-[100dvh] w-full bg-black/50"
        aria-hidden
        onClick={onClose}
      />
      <div
        className={`fixed inset-y-0 ${edge} z-10 flex w-[min(100vw,420px)] max-w-full flex-col bg-white dark:bg-slate-950 shadow-2xl border-slate-200 dark:border-slate-800 ${
          side === "right" ? "border-l" : "border-r"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 shrink-0">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded"
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        ) : null}
        <div className="flex-1 min-h-0 flex flex-col">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Wider panel for forms with tables (e.g. branch schedule). */
  wide?: boolean;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="fixed inset-0 min-h-[100dvh] w-full bg-black/50"
        aria-hidden
        onClick={onClose}
      />
      <div
        className={`relative z-10 bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-h-[min(90dvh,90vh)] flex flex-col ${
          wide ? "max-w-4xl" : "max-w-lg"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <h2
            id="modal-title"
            className="text-base font-semibold text-slate-900 dark:text-white"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-5 flex-1 min-h-0">{children}</div>
        {footer ? (
          <div className="shrink-0 border-t border-slate-200 dark:border-slate-800 px-5 py-3 flex flex-wrap gap-2 justify-end">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

export function ChannelIcon({
  type,
  className = "w-4 h-4",
}: {
  type: string;
  className?: string;
}) {
  if (type === "whatsapp")
    return (
      <svg className={className} viewBox="0 0 24 24" fill="#25D366">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488" />
      </svg>
    );
  if (type === "instagram")
    return (
      <svg className={className} viewBox="0 0 24 24" fill="url(#ig)">
        <defs>
          <linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f09433" />
            <stop offset="25%" stopColor="#e6683c" />
            <stop offset="50%" stopColor="#dc2743" />
            <stop offset="75%" stopColor="#cc2366" />
            <stop offset="100%" stopColor="#bc1888" />
          </linearGradient>
        </defs>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    );
  if (type === "web_anon")
    return (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#6366f1"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
        <g stroke="none">
          <ellipse cx="15.8" cy="15.2" rx="4.4" ry="3.3" fill="#334155" />
          <circle cx="14.1" cy="15" r="1.05" fill="white" />
          <circle cx="17.4" cy="15" r="1.05" fill="white" />
        </g>
      </svg>
    );
  if (type === "web_authed")
    return (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#6366f1"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
        <g transform="translate(11.35 10.35) scale(0.38)" fill="#334155" stroke="none">
          <path d="M17 8h-1V6c0-2.76-2.24-5-5-5S6 3.24 6 6v2H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-2 0H9V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2z" />
        </g>
      </svg>
    );
  if (type === "mercadolibre")
    return (
      <img
        src="/logos/mercadolibre.svg"
        alt=""
        className={`${className} object-contain`}
        aria-hidden
      />
    );
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#6366f1"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  );
}

export function statusBadgeVariant(
  status: string
): keyof typeof BADGE_VARIANTS {
  if (status === "escalated") return "warning";
  if (status === "active") return "primary";
  if (status === "resolved") return "success";
  if (status === "closed") return "gray";
  return "default";
}

export function StatusBadge({
  status,
  className = "",
}: {
  /** API status key (e.g. ``active``, ``escalated``). */
  status: string;
  className?: string;
}) {
  return (
    <Badge variant={statusBadgeVariant(status)} className={className}>
      {statusLabel(status)}
    </Badge>
  );
}

export function StatusDot({ active, label }: { active: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-slate-500">
      <span
        className={`w-2 h-2 rounded-full ${active ? "bg-green-500" : "bg-slate-400"}`}
      />
      {label}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-2xl mb-4">
        {icon ?? "📭"}
      </div>
      <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
        {title}
      </h3>
      {description ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mb-4">
          {description}
        </p>
      ) : null}
      {action ? <div className="flex justify-center">{action}</div> : null}
    </div>
  );
}
